<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import EventSearchBar from '@/components/EventSearch/EventSearchBar.vue'
import ToolSidebar from '@/components/EventSearch/ToolSidebar.vue'
import ExportWarningModal from '@/components/EventSearch/ExportWarningModal.vue'
import Events from '@/api/Events'
import User from '@/api/User'
import SavedSearches from '@/api/SavedSearches'
import { useUserStore } from '@/stores/user'
import { useDataSourcesStore } from '@/stores/dataSources'
import { useFieldSettings } from '@/composables/useFieldSettings'
import {
  FILTER_TYPES,
  createTimeFilter,
  findTimeFilter,
  buildFinalQuery,
  groupQueueItems,
  createFiltersFromQueue,
  filterToQueryString
} from '@/components/EventSearch/filters'
import { selectionToFilterParams, extractTimeFromQuery } from '@/components/EventSearch/timeRangeUtils'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const dataSourcesStore = useDataSourcesStore()
const toast = useToast()

// Check if user has access to event search (requires Elite tier or higher)
const hasEventAccess = computed(() => {
  return userStore.accountType >= 4 || userStore.accountType === 1337
})
const forbidden = ref(false)

// Field settings management (visibility + order)
const {
  isFieldEnabled,
  toggleField,
  getFieldOrder,
  setFieldOrder,
  getOrderedEnabledFields,
  resetLogType,
  hasCustomSettings
} = useFieldSettings()

// Field mappings from store (log type -> fields)
const fieldMappings = computed(() => dataSourcesStore.fieldMappings)

// Sidebar state
const toolSidebarExpanded = ref(false)

// Search state - managed here, passed to children via props
const query = ref('')
const lastExecutedQuery = ref('')  // The query that was last executed (stable base for filters)
const histogram = ref([])
const logTypeDistribution = ref([])
const loading = ref(false)
const exportLoading = ref(false)
const error = ref('')

// Export warning dialog state
const CSV_SAFETY_KEY = 'webscout-csv-safety-dismissed'
const PARTIAL_EXPORT_KEY = 'webscout-partial-export-dismissed'
const showCsvWarning = ref(false)
const showPartialWarning = ref(false)
const pendingExport = ref(null) // { type: 'all'|'logType', logType?, format }
const pageSize = ref(Number(localStorage.getItem('eventSearchPageSize')) || 50)

// Filter system state (time filters only — non-time filters are now part of the query string)
const filters = ref([])

// Time range dropdown state (tracks the dropdown selection separately from the filter)
const searchTimeRange = ref(null)

// Saved search state
const currentSavedSearchId = ref(null)
const currentSavedSearchName = ref('')
const currentSavedSearchDescription = ref('')
const savedSearchQuery = ref('')      // Query at time of load/save
const savedSearchFilters = ref([])    // Filters at time of load/save
const saveLoading = ref(false)

// Subscription state
const isSubscribed = ref(false)
const subscriptionLoading = ref(false)

// Delete state
const deleteLoading = ref(false)

// Computed: whether the search input differs from last executed query
const hasQueryChanges = computed(() => {
  return query.value.trim() !== lastExecutedQuery.value.trim()
})

// Display log type state (which log type is shown in table)
const displayedLogType = ref(null)
const isUserSelection = ref(false)

// Table-specific state (separate from global overview)
const tableResults = ref([])
const tableTotal = ref(0)
const tableCurrentPage = ref(0)
const tableLoading = ref(false)
const tableMoreResults = ref(false)
const tableSort = ref([])  // [{field: "ts", direction: "asc"}, ...]
const searchGeneration = ref(0)
const tableSearchGeneration = ref(0)


// Load a saved search by ID
async function loadSavedSearch(savedId) {
  if (!savedId) return

  loading.value = true
  try {
    const response = await SavedSearches.get(savedId)
    if (response.status === 'not_found') {
      toast.error('Saved search not found')
      router.replace({ path: '/search/event' })
      return
    }
    if (response.status !== 'success') {
      toast.error('Failed to load saved search')
      router.replace({ path: '/search/event' })
      return
    }

    const search = response.data

    // Apply to state
    query.value = search.query
    lastExecutedQuery.value = search.query

    // Parse filters — separate time filters from non-time, merge non-time into query
    const parsedFilters = typeof search.filters === 'string'
      ? JSON.parse(search.filters)
      : search.filters
    const allFilters = parsedFilters || []
    const timeFilters = allFilters.filter(f => f.type === FILTER_TYPES.TIME_RANGE)
    const nonTimeFilters = allFilters.filter(f => f.type !== FILTER_TYPES.TIME_RANGE && f.enabled)

    // Convert non-time filters to Lucene text and merge into query string
    if (nonTimeFilters.length > 0) {
      const filterParts = nonTimeFilters
        .map(f => filterToQueryString(f))
        .filter(s => s.length > 0)
      if (filterParts.length > 0) {
        const filterText = filterParts.join(' AND ')
        const trimmed = query.value.trim()
        query.value = trimmed ? '(' + trimmed + ') AND ' + filterText : filterText
        lastExecutedQuery.value = query.value
      }
    }

    // Only keep time filters
    filters.value = timeFilters

    // Track as current saved search
    currentSavedSearchId.value = search.id
    currentSavedSearchName.value = search.name
    currentSavedSearchDescription.value = search.description || ''
    savedSearchQuery.value = lastExecutedQuery.value
    savedSearchFilters.value = []

    // Track subscription state
    isSubscribed.value = search.is_subscribed || false

    // Check for time filter params from route (e.g., "View New Results" button)
    // Supports both bounded (start + end) and open-ended (start only) time filters
    if (route.query.start) {
      const startMs = parseInt(route.query.start)
      const endMs = route.query.end ? parseInt(route.query.end) : null
      setTimeFilter(startMs, endMs)
    }

    // Append filter from URL if present (e.g., ?filter=field:value)
    if (route.query.filter) {
      applyUrlFilter(route.query.filter)
    }

    // Store logType from URL if present
    if (route.query.logType) {
      urlLogType.value = route.query.logType
    }

    // Mark search as "checked in" only if:
    // - No time filter (regular view), OR
    // - viewNew=true (explicitly viewing new results)
    // This prevents resetting the "new results" badge when just applying other filters
    const hasTimeFilter = !!route.query.start
    const isViewingNewResults = route.query.viewNew === 'true'
    if (!hasTimeFilter || isViewingNewResults) {
      SavedSearches.checkIn(search.id)
    }

    // Execute search without logging (it's a reload of saved)
    await executeSearch(false, true)
  } catch (err) {
    console.error('Error loading saved search:', err)
    toast.error('Failed to load saved search')
    router.replace({ path: '/search/event' })
  } finally {
    loading.value = false
  }
}

// Watch for saved search ID in URL
watch(() => route.query.saved, async (savedId) => {
  if (savedId) {
    await loadSavedSearch(savedId)
  }
}, { immediate: false })

// Store URL-provided log type for setting after search
const urlLogType = ref(null)

// Initialize from route query params
onMounted(async () => {
  // Reset source selection to prevent stale state from prior sessions
  dataSourcesStore.resetSelection()

  // Init data sources store (fetches field mappings etc.) — must await so
  // serializedSources works correctly when syncUrlState runs during executeSearch
  await dataSourcesStore.init()

  // Check for saved search ID first
  // (logType and filter handling is done inside loadSavedSearch)
  if (route.query.saved) {
    await loadSavedSearch(route.query.saved)
    return
  }

  if (route.query.query) {
    query.value = route.query.query
    lastExecutedQuery.value = route.query.query  // Set stable base from route
  }
  // Supports both bounded (start + end) and open-ended (start only) time filters
  if (route.query.start) {
    const startMs = parseInt(route.query.start)
    const endMs = route.query.end ? parseInt(route.query.end) : null
    setTimeFilter(startMs, endMs)
    // Reflect in time range dropdown — preserve preset if passed from widget
    if (route.query.timePreset) {
      searchTimeRange.value = { type: 'preset', value: route.query.timePreset }
    } else {
      searchTimeRange.value = { type: 'custom', start: startMs, end: endMs }
    }
  }
  // Append filter from URL if present (e.g., ?filter=field:value)
  if (route.query.filter) {
    applyUrlFilter(route.query.filter)
  }
  // Store logType from URL if present
  if (route.query.logType) {
    urlLogType.value = route.query.logType
  }
  // Restore source/log-type selections from URL
  restoreSourcesFromUrl(route.query.sources)

  // Execute search if query provided
  if (query.value) {
    const shouldLog = route.query.log !== 'false'
    executeSearch(shouldLog, true)
  }
})

// Parse sources URL param and apply to store
// Format: "srcId:lt1,lt2;srcId2" — colon-separated log types, semicolon-separated sources
function restoreSourcesFromUrl(sourcesParam) {
  if (!sourcesParam) return
  const logTypes = []
  const segments = sourcesParam.split(';')
  for (const seg of segments) {
    const colonIdx = seg.indexOf(':')
    if (colonIdx === -1) {
      // Whole source selected — add all its log types
      const groupLts = dataSourcesStore.sourceLogTypeMap[seg] || []
      logTypes.push(...groupLts)
    } else {
      const lts = seg.substring(colonIdx + 1).split(',').filter(Boolean)
      logTypes.push(...lts)
    }
  }
  if (logTypes.length > 0) {
    const unique = [...new Set(logTypes)]
    dataSourcesStore.selectedLogTypes = unique
    dataSourcesStore.noneSelected = false
    // If all selected, reset to implicit all
    if (unique.length >= dataSourcesStore.allLogTypes.length) {
      dataSourcesStore.selectedLogTypes = []
    }
  }
}

// Reset all search state (used when navigating back to landing)
function resetSearchState() {
  query.value = ''
  lastExecutedQuery.value = ''
  histogram.value = []
  logTypeDistribution.value = []
  tableResults.value = []
  tableTotal.value = 0
  tableCurrentPage.value = 0
  tableSort.value = []
  displayedLogType.value = null
  isUserSelection.value = false
  error.value = ''
  filters.value = []
  searchTimeRange.value = null
  currentSavedSearchId.value = null
  currentSavedSearchName.value = ''
  currentSavedSearchDescription.value = ''
  savedSearchQuery.value = ''
  savedSearchFilters.value = []
  isSubscribed.value = false
  dataSourcesStore.resetSelection()
}

// Build final query with all filters applied (uses stable lastExecutedQuery, not reactive input)
function buildQuery() {
  return buildFinalQuery(lastExecutedQuery.value, filters.value)
}

// Computed final query for child components (includes all filters)
const finalQuery = computed(() => buildFinalQuery(lastExecutedQuery.value, filters.value))

// Append a Lucene expression to the query string and re-execute
function appendToQuery(expression) {
  const trimmed = query.value.trim()
  query.value = trimmed ? '(' + trimmed + ') AND ' + expression : expression
  lastExecutedQuery.value = query.value
  executeSearch(false)
}

// Parse filter from URL (format: "field:value") and append to query
function applyUrlFilter(filterString) {
  if (!filterString || typeof filterString !== 'string') return
  const colonIndex = filterString.indexOf(':')
  if (colonIndex === -1) return
  const field = filterString.substring(0, colonIndex).trim()
  const value = filterString.substring(colonIndex + 1).trim()
  if (!field || !value) return
  const expression = `${field}:"${value.replace(/"/g, '\\"')}"`
  const trimmed = query.value.trim()
  query.value = trimmed ? '(' + trimmed + ') AND ' + expression : expression
  lastExecutedQuery.value = query.value
}

// Filter CRUD operations (time filters only)
function removeFilter(id) {
  filters.value = filters.value.filter(f => f.id !== id)
}

function clearAllFilters() {
  filters.value = []
  searchTimeRange.value = null
}

// Reset query input back to last executed query
function resetQueryToLastExecuted() {
  query.value = lastExecutedQuery.value
}

// Update or create time filter
// endMs is optional - if null, creates an open-ended filter (no end bound)
function setTimeFilter(startMs, endMs = null) {
  const existingTimeFilter = findTimeFilter(filters.value)
  if (existingTimeFilter) {
    existingTimeFilter.values = { start: startMs, end: endMs }
    existingTimeFilter.enabled = true
  } else {
    filters.value.push(createTimeFilter(startMs, endMs))
  }
}

// Remove time filter
function removeTimeFilter() {
  const tf = findTimeFilter(filters.value)
  if (tf) {
    removeFilter(tf.id)
  }
}

// Auto-select log type from distribution (pick the one with most results)
function autoSelectLogType() {
  if (logTypeDistribution.value.length === 0) {
    displayedLogType.value = null
    return null
  }

  // Sort by count descending, pick first
  const sorted = [...logTypeDistribution.value].sort((a, b) => b.count - a.count)
  displayedLogType.value = sorted[0].log_type
  return sorted[0].log_type
}

// Execute table-specific search (for the displayed log type)
async function executeTableSearch(logType, page = 0) {
  if (!logType || !query.value) {
    tableResults.value = []
    tableTotal.value = 0
    tableMoreResults.value = false
    return
  }

  const generation = ++tableSearchGeneration.value
  tableLoading.value = true
  tableCurrentPage.value = page

  try {
    const finalQuery = buildQuery()
    const offset = page * pageSize.value
    const response = await Events.search(finalQuery, pageSize.value, offset, [logType], tableSort.value)

    if (generation !== tableSearchGeneration.value) return

    if (response.status === 'success') {
      tableResults.value = response.data.results || []
      tableTotal.value = response.data.total || 0

      const totalPages = Math.ceil(tableTotal.value / pageSize.value)
      tableMoreResults.value = page < totalPages - 1
    } else if (response.status === 'timeout') {
      error.value = response.message || 'Query timeout - too many results. Try narrowing your time range or adding more specific filters.'
    } else if (response.status === 'error') {
      error.value = response.message || 'Invalid query syntax'
    }
  } catch (err) {
    if (generation !== tableSearchGeneration.value) return
    error.value = 'An unexpected error occurred'
    console.error('Table search error:', err)
  } finally {
    if (generation === tableSearchGeneration.value) {
      tableLoading.value = false
    }
  }
}

// URL state synchronization — pushes current search state to the URL for back/forward support
let skipRouteWatch = false

async function syncUrlState(replace = false) {
  const params = {}
  if (lastExecutedQuery.value) params.query = lastExecutedQuery.value
  if (currentSavedSearchId.value) params.saved = String(currentSavedSearchId.value)

  // Time filter params
  const tf = findTimeFilter(filters.value)
  if (tf?.enabled) {
    params.start = String(tf.values.start)
    if (tf.values.end != null) params.end = String(tf.values.end)
  }
  if (searchTimeRange.value?.type === 'preset') {
    params.timePreset = searchTimeRange.value.value
  }

  // Source / log-type selection params (omitted when "all")
  const sources = dataSourcesStore.serializedSources
  if (sources) {
    params.sources = sources
  }

  // Ensure we navigate to results view when executing a search
  const path = lastExecutedQuery.value
    ? (route.path === '/search/event' ? '/search/event/table' : route.path)
    : route.path

  skipRouteWatch = true
  try {
    const nav = { path, query: params }
    if (replace) await router.replace(nav)
    else await router.push(nav)
    await nextTick()
  } finally {
    skipRouteWatch = false
  }
}

// Re-sync URL state when switching between child views (table <-> aggregations)
// The layout owns the search state, so it re-pushes params onto the new path.
// When navigating to the landing page, clear all state instead.
watch(() => route.path, (newPath, oldPath) => {
  if (skipRouteWatch) return
  if (newPath === oldPath) return
  // Only act when still within the event search layout
  if (!newPath.startsWith('/search/event')) return

  // Navigating to landing — clear all search state
  if (newPath === '/search/event') {
    resetSearchState()
    return
  }

  nextTick(() => syncUrlState(true))
})

// Restore state on back/forward navigation
watch(() => route.query, (newQuery, oldQuery) => {
  if (skipRouteWatch) return
  if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return
  if (newQuery.saved) return // Saved searches handled by their own watcher

  // No query in URL — reset everything (e.g. browser back to landing)
  if (!newQuery.query) {
    resetSearchState()
    return
  }

  query.value = newQuery.query
  lastExecutedQuery.value = newQuery.query

  // Restore time filter
  removeTimeFilter()
  searchTimeRange.value = null
  if (newQuery.start) {
    const startMs = parseInt(newQuery.start)
    const endMs = newQuery.end ? parseInt(newQuery.end) : null
    setTimeFilter(startMs, endMs)
    searchTimeRange.value = newQuery.timePreset
      ? { type: 'preset', value: newQuery.timePreset }
      : { type: 'custom', start: startMs, end: endMs }
  }

  // Restore source/log-type selections
  if (newQuery.sources) {
    restoreSourcesFromUrl(newQuery.sources)
  } else {
    dataSourcesStore.resetSelection()
  }

  executeSearch(false, true)
}, { deep: true })

// Execute global search (for histogram and log type distribution)
async function executeSearch(shouldLog = true, replace = false) {
  if (!hasEventAccess.value) {
    forbidden.value = true
    loading.value = false
    return
  }

  const generation = ++searchGeneration.value

  // Extract inline ts:[...] from query — normalizes time into the filter system
  // regardless of entry point (typed query, history, URL)
  const { cleanQuery, start: tsStart, end: tsEnd } = extractTimeFromQuery(query.value)
  if (tsStart !== null) {
    query.value = cleanQuery
    lastExecutedQuery.value = cleanQuery
    setTimeFilter(tsStart, tsEnd)
    searchTimeRange.value = { type: 'custom', start: tsStart, end: tsEnd }
  }

  // When executing a new search (shouldLog=true), update the stable base query
  if (shouldLog) {
    lastExecutedQuery.value = query.value
  }

  const finalQuery = buildQuery()

  // Sync current state to URL for back/forward navigation
  await syncUrlState(replace)

  // Log the query to history
  if (shouldLog && lastExecutedQuery.value) {
    await User.save_query([finalQuery], 'event_search')
    userStore.getQueries('event')
  }

  // Reset state
  error.value = ''
  loading.value = true

  // Clear previous data
  histogram.value = []
  logTypeDistribution.value = []
  tableResults.value = []
  tableTotal.value = 0
  tableCurrentPage.value = 0

  // Reset user selection on new search query (shouldLog=true means new query)
  if (shouldLog) {
    displayedLogType.value = null
    isUserSelection.value = false
  }

  try {
    // Execute global search (filtered by source/log selection) for overview data
    const response = await Events.search(finalQuery, 1, 0, dataSourcesStore.effectiveLogTypes)

    if (generation !== searchGeneration.value) return

    if (response.status === 'success') {
      // Store overview data
      histogram.value = response.data.histogram || []
      logTypeDistribution.value = response.data.log_types || []

      // Determine which log type to display
      let selectedLogType = null

      // Check if URL provided a log type
      if (urlLogType.value) {
        const urlLogTypeData = logTypeDistribution.value.find(
          lt => lt.log_type === urlLogType.value
        )
        if (urlLogTypeData && urlLogTypeData.count > 0) {
          // Use URL-provided log type if it has results
          selectedLogType = urlLogType.value
          displayedLogType.value = urlLogType.value
          isUserSelection.value = true
          urlLogType.value = null  // Clear after use
        }
      }

      // If no URL log type or it didn't have results, use existing logic
      if (!selectedLogType) {
        if (isUserSelection.value && displayedLogType.value) {
          // Check if user's selection still has results
          const userLogTypeData = logTypeDistribution.value.find(
            lt => lt.log_type === displayedLogType.value
          )

          if (userLogTypeData && userLogTypeData.count > 0) {
            // Keep user's selection - it still has results
            selectedLogType = displayedLogType.value
          } else {
            // User's selection has no results - fall back to auto-select
            isUserSelection.value = false
            selectedLogType = autoSelectLogType()
          }
        } else {
          // No user selection - auto-select
          selectedLogType = autoSelectLogType()
        }
      }

      if (selectedLogType) {
        await executeTableSearch(selectedLogType)
      }
    } else if (response.status === 'timeout') {
      error.value = response.message || 'Query timeout - too many results. Try narrowing your time range or adding more specific filters.'
    } else if (response.status === 'error') {
      error.value = response.message || 'Invalid query syntax'
    } else if (response.status === 'ratelimited') {
      error.value = 'Too many requests - please wait and try again'
    } else if (response.status === 'unauthorized') {
      error.value = 'Unauthorized - admin access required'
    } else {
      error.value = 'Search failed - please try again'
    }
  } catch (err) {
    if (generation !== searchGeneration.value) return
    error.value = 'An unexpected error occurred'
    console.error('Search error:', err)
  } finally {
    if (generation === searchGeneration.value) {
      loading.value = false
    }
  }
}

// Handle log type selection from sidebar
function handleLogTypeSelect(logType) {
  if (displayedLogType.value === logType) return

  displayedLogType.value = logType
  isUserSelection.value = true
  tableCurrentPage.value = 0
  tableSort.value = []  // Reset sort when changing log type
  executeTableSearch(logType)
}

// Navigate to next page (table)
async function tableNextPage() {
  if (!tableMoreResults.value || tableLoading.value) return
  await executeTableSearch(displayedLogType.value, tableCurrentPage.value + 1)
}

// Navigate to previous page (table)
async function tablePrevPage() {
  if (tableCurrentPage.value === 0 || tableLoading.value) return
  await executeTableSearch(displayedLogType.value, tableCurrentPage.value - 1)
}

// Change page size
function handlePageSizeChange(size) {
  pageSize.value = size
  localStorage.setItem('eventSearchPageSize', size)
  tableCurrentPage.value = 0
  if (displayedLogType.value) {
    executeTableSearch(displayedLogType.value, 0)
  }
}

// Check if export exceeds 10k limit
function exportExceedsLimit(type, logType) {
  if (type === 'logType' && logType) {
    const dist = logTypeDistribution.value.find(d => d.log_type === logType)
    return dist && dist.count > 10000
  }
  // "all" — check total across all log types
  const total = logTypeDistribution.value.reduce((sum, d) => sum + d.count, 0)
  return total > 10000
}

// Initiate export with warning dialogs if needed
function exportResults(format = 'jsonl') {
  startExportWithWarnings({ type: 'all', format })
}

function exportLogType(logType, format = 'jsonl') {
  startExportWithWarnings({ type: 'logType', logType, format })
}

function startExportWithWarnings(exportInfo) {
  pendingExport.value = exportInfo

  // Check CSV warning first
  if (exportInfo.format === 'csv' && localStorage.getItem(CSV_SAFETY_KEY) !== 'true') {
    showCsvWarning.value = true
    return
  }

  // Then check partial results warning
  if (exportExceedsLimit(exportInfo.type, exportInfo.logType) && localStorage.getItem(PARTIAL_EXPORT_KEY) !== 'true') {
    showPartialWarning.value = true
    return
  }

  // No warnings needed, proceed
  executeExport(exportInfo)
}

function onCsvWarningConfirm() {
  showCsvWarning.value = false
  const exportInfo = pendingExport.value
  if (!exportInfo) return

  // Check if partial warning also needed
  if (exportExceedsLimit(exportInfo.type, exportInfo.logType) && localStorage.getItem(PARTIAL_EXPORT_KEY) !== 'true') {
    showPartialWarning.value = true
    return
  }

  executeExport(exportInfo)
}

function onPartialWarningConfirm() {
  showPartialWarning.value = false
  const exportInfo = pendingExport.value
  if (!exportInfo) return
  executeExport(exportInfo)
}

function onExportWarningCancel() {
  showCsvWarning.value = false
  showPartialWarning.value = false
  pendingExport.value = null
}

// Execute the actual export API call
async function executeExport(exportInfo) {
  pendingExport.value = null
  exportLoading.value = true
  error.value = ''

  try {
    const finalQuery = buildFinalQuery(lastExecutedQuery.value, filters.value)
    const logTypes = exportInfo.type === 'logType' ? [exportInfo.logType] : dataSourcesStore.effectiveLogTypes
    const label = exportInfo.type === 'logType' ? `Export started for ${exportInfo.logType}` : 'Export started - download will begin shortly'
    toast.success(label)
    const response = await Events.exportDocuments(finalQuery, logTypes, exportInfo.format)

    if (response.status === 'ratelimited') {
      toast.error('Too many requests - please wait and try again')
    } else if (response.status === 'unauthorized') {
      toast.error('Unauthorized - admin access required')
    } else if (response.status === 'timeout') {
      toast.error('Export timeout - too many results. Try narrowing your query.')
    } else if (response.status !== 'success') {
      toast.error('Export failed - please try again')
    }
  } catch (err) {
    toast.error('Export failed')
    console.error('Export error:', err)
  } finally {
    exportLoading.value = false
  }
}

// Save as new search
async function handleSaveSearch({ name, description }) {
  saveLoading.value = true
  try {
    const response = await SavedSearches.create(
      name,
      description,
      lastExecutedQuery.value,
      []
    )
    if (response.status === 'success') {
      currentSavedSearchId.value = response.data.id
      currentSavedSearchName.value = name
      currentSavedSearchDescription.value = description
      savedSearchQuery.value = lastExecutedQuery.value
      savedSearchFilters.value = []
      isSubscribed.value = false
      toast.success('Search saved successfully')
    } else {
      toast.error(response.message || 'Failed to save search')
    }
  } catch (err) {
    toast.error('Failed to save search')
    console.error('Save search error:', err)
  } finally {
    saveLoading.value = false
  }
}

// Subscribe to search updates
async function handleSubscribe() {
  if (!currentSavedSearchId.value) return

  subscriptionLoading.value = true
  try {
    const response = await SavedSearches.subscribe(currentSavedSearchId.value)
    if (response.status === 'success') {
      isSubscribed.value = true
      toast.success('Subscribed to search updates')
    } else {
      toast.error(response.message || 'Failed to subscribe')
    }
  } catch (err) {
    toast.error('Failed to subscribe')
    console.error('Subscribe error:', err)
  } finally {
    subscriptionLoading.value = false
  }
}

// Unsubscribe from search updates
async function handleUnsubscribe() {
  if (!currentSavedSearchId.value) return

  subscriptionLoading.value = true
  try {
    const response = await SavedSearches.unsubscribe(currentSavedSearchId.value)
    if (response.status === 'success') {
      isSubscribed.value = false
      toast.success('Unsubscribed from search updates')
    } else {
      toast.error(response.message || 'Failed to unsubscribe')
    }
  } catch (err) {
    toast.error('Failed to unsubscribe')
    console.error('Unsubscribe error:', err)
  } finally {
    subscriptionLoading.value = false
  }
}

// Overwrite existing search
async function handleOverwriteSearch({ name, description }) {
  if (!currentSavedSearchId.value) return

  saveLoading.value = true
  try {
    const response = await SavedSearches.update(
      currentSavedSearchId.value,
      name,
      description,
      lastExecutedQuery.value,
      []
    )
    if (response.status === 'success') {
      currentSavedSearchName.value = name
      currentSavedSearchDescription.value = description
      savedSearchQuery.value = lastExecutedQuery.value
      savedSearchFilters.value = []
      toast.success('Search updated successfully')
    } else {
      toast.error(response.message || 'Failed to update search')
    }
  } catch (err) {
    toast.error('Failed to update search')
    console.error('Update search error:', err)
  } finally {
    saveLoading.value = false
  }
}

// Delete saved search
async function handleDeleteSearch() {
  if (!currentSavedSearchId.value) return

  deleteLoading.value = true
  try {
    const response = await SavedSearches.delete(currentSavedSearchId.value)
    if (response.status === 'success') {
      // Clear saved search state (keep query and filters)
      currentSavedSearchId.value = null
      currentSavedSearchName.value = ''
      currentSavedSearchDescription.value = ''
      savedSearchQuery.value = ''
      savedSearchFilters.value = []
      isSubscribed.value = false
      // Remove the saved query param from URL
      router.replace({ path: '/search/event' })
      toast.success('Search deleted')
    } else {
      toast.error(response.message || 'Failed to delete search')
    }
  } catch (err) {
    toast.error('Failed to delete search')
    console.error('Delete search error:', err)
  } finally {
    deleteLoading.value = false
  }
}

// Handle time filter updates from child (histogram selection)
function handleTimeFilterUpdate(newFilter) {
  setTimeFilter(newFilter.start, newFilter.end)
  searchTimeRange.value = { type: 'custom', start: newFilter.start, end: newFilter.end }
  executeSearch(false)
}

// Handle time range dropdown selection
function handleTimeRangeChange(selection) {
  searchTimeRange.value = selection
  const params = selectionToFilterParams(selection)
  if (params) {
    setTimeFilter(params.start, params.end)
  } else {
    removeTimeFilter()
  }
  // Only re-execute if there's an active search (lastExecutedQuery set)
  if (lastExecutedQuery.value.trim()) {
    executeSearch(false, true)
  }
}

// Handle search from search bar
function handleSearch() {
  executeSearch(true)
}

// Handle filter application from table (ctrl-click queue)
function handleApplyFilter(queueItems, operator) {
  const grouped = groupQueueItems(queueItems)
  const newFilters = createFiltersFromQueue(grouped, operator)
  const parts = newFilters
    .map(f => filterToQueryString(f))
    .filter(s => s.length > 0)
  if (parts.length > 0) {
    appendToQuery(parts.join(' AND '))
  }
}

// Handle single filter addition from column stats popover
function handleAddSingleFilter(filterData) {
  // filterData: { field, value, operator }
  const value = String(filterData.value).replace(/"/g, '\\"')
  appendToQuery(`${filterData.field}:"${value}"`)
}

// Sortable field types in Elasticsearch
const SORTABLE_TYPES = new Set([
  'keyword', 'date', 'long', 'integer', 'short', 'byte',
  'double', 'float', 'half_float', 'scaled_float', 'boolean', 'ip'
])

// Handle sort change from table header click
// fieldInfo: { type: string, hasKeyword: boolean }
function handleSortChange(field, fieldInfo) {
  const { type: fieldType, hasKeyword } = fieldInfo

  // Check if field is sortable (native sortable type OR text with keyword sub-field)
  const isSortable = SORTABLE_TYPES.has(fieldType) || (fieldType === 'text' && hasKeyword)

  if (!isSortable) {
    toast.error(`Cannot sort by "${field}" - ${fieldType} fields are not sortable`)
    return
  }

  // Determine if we need to use .keyword suffix
  const useKeyword = fieldType === 'text' && hasKeyword

  const existingIndex = tableSort.value.findIndex(s => s.field === field)

  if (existingIndex === -1) {
    // First click: add ascending
    tableSort.value = [...tableSort.value, { field, direction: 'asc', use_keyword: useKeyword }]
  } else if (tableSort.value[existingIndex].direction === 'asc') {
    // Second click: change to descending
    tableSort.value = tableSort.value.map((s, i) =>
      i === existingIndex ? { ...s, direction: 'desc' } : s
    )
  } else {
    // Third click: remove
    tableSort.value = tableSort.value.filter((_, i) => i !== existingIndex)
  }

  // Re-fetch table data from page 1
  tableCurrentPage.value = 0
  executeTableSearch(displayedLogType.value, 0)
}

// Handle query selection from history view or landing page
async function handleHistoryQuerySelect(queryText, timePreset) {
  query.value = queryText
  dataSourcesStore.resetSelection()
  if (timePreset) {
    const now = Date.now()
    const presetMs = { '1h': 3600000, '6h': 21600000, '24h': 86400000, '7d': 604800000, '30d': 2592000000 }
    const start = now - (presetMs[timePreset] || presetMs['7d'])
    setTimeFilter(start, null)
    searchTimeRange.value = { type: 'preset', value: timePreset }
  }
  skipRouteWatch = true
  await router.push({ path: '/search/event/table' })
  await nextTick()
  skipRouteWatch = false
  executeSearch(true, true)
}

// Handle log type exploration from landing page (exclusively select log type, 7d window)
async function handleExploreLogType(logType) {
  dataSourcesStore.selectExclusiveLogType(logType)
  query.value = '*'
  const now = Date.now()
  const start = now - 7 * 24 * 60 * 60 * 1000
  setTimeFilter(start, null)
  searchTimeRange.value = { type: 'preset', value: '7d' }

  skipRouteWatch = true
  await router.push({ path: '/search/event/table' })
  await nextTick()
  skipRouteWatch = false
  executeSearch(true, true)
}

// Handle source exploration from landing page (exclusively select source, 7d window)
async function handleExploreSource(sourceId) {
  dataSourcesStore.selectExclusiveSource(sourceId)
  query.value = '*'
  const now = Date.now()
  const start = now - 7 * 24 * 60 * 60 * 1000
  setTimeFilter(start, null)
  searchTimeRange.value = { type: 'preset', value: '7d' }

  skipRouteWatch = true
  await router.push({ path: '/search/event/table' })
  await nextTick()
  skipRouteWatch = false
  executeSearch(true, true)
}

// Handle navigation to aggregations view (from column stats popover)
function handleNavigateAggregations(field) {
  const params = { field }
  if (lastExecutedQuery.value) params.query = lastExecutedQuery.value

  const tf = findTimeFilter(filters.value)
  if (tf?.enabled) {
    params.start = String(tf.values.start)
    if (tf.values.end != null) params.end = String(tf.values.end)
  }
  if (searchTimeRange.value?.type === 'preset') {
    params.timePreset = searchTimeRange.value.value
  }

  router.push({ name: 'EventSearchAggregations', query: params })
}
</script>

<template>
  <div class="event-search-layout">
    <!-- Top Search Bar -->
    <EventSearchBar
      v-model="query"
      :loading="loading"
      :has-query-changes="hasQueryChanges"
      :time-range="searchTimeRange"
      @search="handleSearch"
      @reset-query="resetQueryToLastExecuted"
      @time-range-change="handleTimeRangeChange"
    />

    <!-- Main Content Area -->
    <div class="event-search-main">
      <!-- Content (router-view for nested routes) -->
      <div class="event-search-content">
        <router-view
          :query="finalQuery"
          :base-query="lastExecutedQuery"
          :histogram="histogram"
          :log-type-distribution="logTypeDistribution"
          :displayed-log-type="displayedLogType"
          :loading="loading"
          :error="error"
          :forbidden="forbidden"
          :table-results="tableResults"
          :table-total="tableTotal"
          :table-current-page="tableCurrentPage"
          :table-loading="tableLoading"
          :table-more-results="tableMoreResults"
          :table-sort="tableSort"
          :page-size="pageSize"
          :field-mappings="fieldMappings"
          :is-field-enabled="isFieldEnabled"
          :toggle-field="toggleField"
          :get-field-order="getFieldOrder"
          :set-field-order="setFieldOrder"
          :get-ordered-enabled-fields="getOrderedEnabledFields"
          :reset-log-type="resetLogType"
          :has-custom-settings="hasCustomSettings"
          @update:time-filter="handleTimeFilterUpdate"
          @select-log-type="handleLogTypeSelect"
          @table-next-page="tableNextPage"
          @table-prev-page="tablePrevPage"
          @apply-filter="handleApplyFilter"
          @sort-change="handleSortChange"
          @add-filter="handleAddSingleFilter"
          @update:page-size="handlePageSizeChange"
          @navigate-aggregations="handleNavigateAggregations"
          @execute-query="handleHistoryQuerySelect"
          @explore-log-type="handleExploreLogType"
          @explore-source="handleExploreSource"
        />
      </div>

      <!-- Right Tool Sidebar -->
      <ToolSidebar
        :has-executed-query="!!lastExecutedQuery"
        :can-export="tableResults.length > 0 || logTypeDistribution.length > 0"
        :loading="loading"
        :export-loading="exportLoading"
        :log-type-distribution="logTypeDistribution"
        :saved-search-id="currentSavedSearchId"
        :saved-search-name="currentSavedSearchName"
        :saved-search-description="currentSavedSearchDescription"
        :saved-search-query="savedSearchQuery"
        :current-query="lastExecutedQuery"
        :save-loading="saveLoading"
        :delete-loading="deleteLoading"
        :is-subscribed="isSubscribed"
        :subscription-loading="subscriptionLoading"
        @update:expanded="toolSidebarExpanded = $event"
        @export="exportResults"
        @export-log-type="exportLogType"
        @save-search="handleSaveSearch"
        @overwrite-search="handleOverwriteSearch"
        @delete-search="handleDeleteSearch"
        @subscribe="handleSubscribe"
        @unsubscribe="handleUnsubscribe"
      />
    </div>

    <!-- CSV Safety Warning -->
    <ExportWarningModal
      :show="showCsvWarning"
      :icon="['fas', 'shield-halved']"
      title="CSV Export Safety Notice"
      :storage-key="CSV_SAFETY_KEY"
      @confirm="onCsvWarningConfirm"
      @cancel="onExportWarningCancel"
    >
      <p>Export data should be treated as <strong>untrusted</strong>. When opening CSV files in spreadsheet programs (Excel, Google Sheets, LibreOffice Calc), <strong>disable automatic formula execution</strong> to prevent potential security risks.</p>
      <p>Values starting with <code>=</code> <code>+</code> <code>-</code> <code>@</code> or <code>|</code> are prefixed with <code>[PROT]</code> to mitigate most formula injection attacks.</p>
    </ExportWarningModal>

    <!-- Partial Export Warning -->
    <ExportWarningModal
      :show="showPartialWarning"
      :icon="['fas', 'triangle-exclamation']"
      title="Partial Export Warning"
      :storage-key="PARTIAL_EXPORT_KEY"
      @confirm="onPartialWarningConfirm"
      @cancel="onExportWarningCancel"
    >
      <p>Your query matches more than <strong>10,000</strong> documents. The export will contain only the <strong>first 10,000 results</strong>.</p>
      <p>Consider narrowing your query or filtering by log type to get more complete results.</p>
    </ExportWarningModal>
  </div>
</template>

<style scoped>
.event-search-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
}

.event-search-main {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.event-search-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 1rem;
  padding-left: 0;
  padding-bottom: 0;
  overflow: hidden;
}

.event-search-content :deep(> *) {
  flex: 1;
  min-height: 0;
}
</style>
